import { Hand, UserCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useOfferAssistance,
  getListInventoryQueryKey,
  getListEventInventoryQueryKey,
  type InventoryListing,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

interface NeedHelpRowCardProps {
  row: InventoryListing;
  onClaimed: () => void;
}

function NeedHelpRowCard({ row, onClaimed }: NeedHelpRowCardProps) {
  const queryClient = useQueryClient();
  const [done, setDone] = useState(false);
  const mutation = useOfferAssistance({
    mutation: {
      onSuccess: () => {
        setDone(true);
        queryClient.invalidateQueries({ queryKey: getListInventoryQueryKey() });
        if (row.eventId != null) {
          queryClient.invalidateQueries({
            queryKey: getListEventInventoryQueryKey(row.eventId),
          });
        }
        onClaimed();
      },
    },
  });

  const seatLabel = (() => {
    if (row.seatFrom && row.seatTo && row.seatFrom !== row.seatTo) {
      return `Seats ${row.seatFrom}–${row.seatTo}`;
    }
    if (row.seatFrom) return `Seat ${row.seatFrom}`;
    return null;
  })();

  const pullerLabel = row.pullerName
    ? `${row.pullerName}${row.pullerTag ? ` (${row.pullerTag})` : ""}`
    : (row.pullerTag ?? "Unknown puller");

  if (done) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5 text-[12px] text-emerald-300">
        <UserCheck className="h-4 w-4 shrink-0" />
        <span>Claimed — listing is now in your workspace.</span>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-orange-500/30 bg-orange-500/10 px-3 py-2.5">
      <div className="min-w-0 space-y-0.5">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] font-medium text-foreground">
          <span>{row.performer ?? "Event"}</span>
          {row.section && (
            <>
              <span className="text-muted-foreground">·</span>
              <span>
                Sec {row.section}
                {row.row ? ` / Row ${row.row}` : ""}
              </span>
            </>
          )}
          {seatLabel && (
            <>
              <span className="text-muted-foreground">·</span>
              <span>{seatLabel}</span>
            </>
          )}
          {row.availableQuantity != null && row.availableQuantity > 0 && (
            <>
              <span className="text-muted-foreground">·</span>
              <span>{row.availableQuantity} qty</span>
            </>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground">
          From <span className="text-orange-300">{pullerLabel}</span>
        </div>
        {row.needHelpNote && (
          <p className="text-[11px] italic text-orange-200/80">{row.needHelpNote}</p>
        )}
      </div>
      <Button
        size="sm"
        className="h-7 shrink-0 text-[11px] bg-orange-600 hover:bg-orange-500 text-white"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate({ id: row.id })}
      >
        {mutation.isPending ? (
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
        ) : (
          <UserCheck className="h-3 w-3 mr-1" />
        )}
        Offer Assistance
      </Button>
    </div>
  );
}

interface NeedHelpSectionProps {
  rows: InventoryListing[];
  onClaimed: () => void;
}

export function NeedHelpSection({ rows, onClaimed }: NeedHelpSectionProps) {
  if (rows.length === 0) return null;
  return (
    <div className="mb-3 rounded-lg border border-orange-500/40 bg-background/50 p-3">
      <div className="mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-orange-400">
        <Hand className="h-3.5 w-3.5" />
        <span>
          {rows.length === 1
            ? "1 listing needs your help"
            : `${rows.length} listings need your help`}
        </span>
      </div>
      <div className="space-y-2">
        {rows.map((row) => (
          <NeedHelpRowCard key={row.id} row={row} onClaimed={onClaimed} />
        ))}
      </div>
    </div>
  );
}
